/**
 * PDF Layout Engine
 *
 * Generates professional PDF layouts for different page types.
 * Coordinates with specific layout templates to create structured
 * content optimized for printing and sharing.
 */

import { DaysListPDFLayout } from '../layouts/DaysListPDFLayout.js';
import { PlacesListPDFLayout } from '../layouts/PlacesListPDFLayout.js';
import { TravelListPDFLayout } from '../layouts/TravelListPDFLayout.js';
import { AccommodationListPDFLayout } from '../layouts/AccommodationListPDFLayout.js';
import { DocumentsListPDFLayout } from '../layouts/DocumentsListPDFLayout.js';
import { DetailPagePDFLayout } from '../layouts/DetailPagePDFLayout.js';

class PDFLayoutEngine {
  constructor() {
    this.layoutTemplates = new Map();
    this.initializeLayouts();
  }

  /**
   * Initialize layout template instances
   */
  initializeLayouts() {
    this.layoutTemplates.set('Days List', new DaysListPDFLayout());
    this.layoutTemplates.set('Places List', new PlacesListPDFLayout());
    this.layoutTemplates.set('Travel List', new TravelListPDFLayout());
    this.layoutTemplates.set('Accommodation List', new AccommodationListPDFLayout());
    this.layoutTemplates.set('Documents List', new DocumentsListPDFLayout());

    // Detail pages use the same layout template but with different data
    this.layoutTemplates.set('Day Detail', new DetailPagePDFLayout());
    this.layoutTemplates.set('Place Detail', new DetailPagePDFLayout());
    this.layoutTemplates.set('Travel Detail', new DetailPagePDFLayout());
    this.layoutTemplates.set('Accommodation Detail', new DetailPagePDFLayout());
    this.layoutTemplates.set('Document Detail', new DetailPagePDFLayout());
    this.layoutTemplates.set('Extras List', new DetailPagePDFLayout());
  }

  /**
   * Generate PDF layout structure for the given page context
   * @param {Object} pageContext - Page context from pageContextDetector
   * @returns {Promise<Object>} PDF content structure
   */
  async generateLayout(pageContext) {
    try {
      const layoutTemplate = this.getLayoutTemplate(pageContext.type);

      if (!layoutTemplate) {
        throw new Error(`No layout template found for page type: ${pageContext.type}`);
      }

      // Generate layout using appropriate template
      const content = await layoutTemplate.generate(pageContext);

      // Add common elements
      return this.enhanceWithCommonElements(content, pageContext);

    } catch (error) {
      console.error('PDF layout generation failed:', error);
      throw error;
    }
  }

  /**
   * Get appropriate layout template for page type
   * @param {string} pageType - Page type identifier
   * @returns {Object} Layout template instance
   */
  getLayoutTemplate(pageType) {
    return this.layoutTemplates.get(pageType);
  }

  /**
   * Enhance content with common elements - SIMPLIFIED per specification
   * @param {Object} content - Generated content structure
   * @param {Object} pageContext - Page context
   * @returns {Object} Enhanced content structure
   */
  enhanceWithCommonElements(content, pageContext) {
    return {
      ...content,
      header: this.generateSimpleHeader(pageContext),
      // NO FOOTER per specification - clean simple PDFs only
      metadata: this.generateMetadata(pageContext)
    };
  }

  /**
   * Generate simple header for all PDF types - SIMPLIFIED per specification
   * @param {Object} pageContext - Page context
   * @returns {Object} Header configuration
   */
  generateSimpleHeader(pageContext) {
    return {
      title: pageContext.title,
      // NO subtitle, dates, or other complex elements per spec
      tripName: pageContext.tripName
    };
  }

  // FOOTER GENERATION REMOVED per specification - clean simple PDFs only

  /**
   * Generate PDF metadata
   * @param {Object} pageContext - Page context
   * @returns {Object} Metadata configuration
   */
  generateMetadata(pageContext) {
    return {
      title: pageContext.title,
      author: 'Trip Planner Application',
      subject: `${pageContext.type} export from ${pageContext.tripName}`,
      keywords: ['travel', 'itinerary', 'trip-planner', pageContext.type.toLowerCase()],
      creator: 'Trip Planner PDF Export',
      createdAt: new Date().toISOString(),
      tripId: pageContext.tripId,
      userId: pageContext.userId,
      pageType: pageContext.type
    };
  }

  /**
   * Generate subtitle based on page context
   * @param {Object} pageContext - Page context
   * @returns {string} Generated subtitle
   */
  generateSubtitle(pageContext) {
    const dateRange = this.extractTripDates(pageContext);

    switch (pageContext.type) {
      case 'Days List':
        return `Complete daily itinerary${dateRange ? ` (${dateRange})` : ''}`;
      case 'Day Detail':
        return `Detailed day information${dateRange ? ` (${dateRange})` : ''}`;
      case 'Places List':
        return `Places and locations to visit`;
      case 'Place Detail':
        return `Detailed place information`;
      case 'Travel List':
        return `Transportation arrangements and bookings`;
      case 'Travel Detail':
        return `Detailed travel information`;
      case 'Accommodation List':
        return `Lodging and accommodation details`;
      case 'Accommodation Detail':
        return `Detailed accommodation information`;
      case 'Documents List':
        return `Travel documents and attachments`;
      case 'Document Detail':
        return `Detailed document information`;
      case 'Extras List':
        return `Additional trip information and notes`;
      default:
        return `Trip information export`;
    }
  }

  /**
   * Extract trip date range from context data
   * @param {Object} pageContext - Page context
   * @returns {string} Formatted date range
   */
  extractTripDates(pageContext) {
    try {
      let dates = [];

      // Try to extract dates from different data sources
      if (pageContext.type === 'Days List' && Array.isArray(pageContext.data)) {
        dates = pageContext.data
          .map(day => day.date)
          .filter(date => date)
          .map(date => new Date(date))
          .sort((a, b) => a - b);
      } else if (pageContext.type === 'Day Detail' && pageContext.data?.date) {
        dates = [new Date(pageContext.data.date)];
      }

      if (dates.length === 0) {
        return null;
      }

      if (dates.length === 1) {
        return dates[0].toLocaleDateString();
      }

      const startDate = dates[0];
      const endDate = dates[dates.length - 1];

      if (startDate.getTime() === endDate.getTime()) {
        return startDate.toLocaleDateString();
      }

      return `${startDate.toLocaleDateString()} - ${endDate.toLocaleDateString()}`;

    } catch (error) {
      console.warn('Could not extract trip dates:', error);
      return null;
    }
  }

  /**
   * Validate that layout is supported
   * @param {string} pageType - Page type to validate
   * @returns {boolean} Whether layout is supported
   */
  isLayoutSupported(pageType) {
    return this.layoutTemplates.has(pageType);
  }

  /**
   * Get list of supported layout types
   * @returns {Array<string>} Supported layout types
   */
  getSupportedLayouts() {
    return Array.from(this.layoutTemplates.keys());
  }

  /**
   * Register custom layout template
   * @param {string} pageType - Page type identifier
   * @param {Object} layoutTemplate - Layout template instance
   */
  registerLayout(pageType, layoutTemplate) {
    if (!layoutTemplate || typeof layoutTemplate.generate !== 'function') {
      throw new Error('Layout template must have a generate method');
    }

    this.layoutTemplates.set(pageType, layoutTemplate);
  }

  /**
   * Remove layout template
   * @param {string} pageType - Page type identifier
   */
  unregisterLayout(pageType) {
    this.layoutTemplates.delete(pageType);
  }

  /**
   * Get layout preview information
   * @param {string} pageType - Page type
   * @param {*} data - Data to analyze
   * @returns {Object} Layout preview information
   */
  getLayoutPreview(pageType, data) {
    const layoutTemplate = this.getLayoutTemplate(pageType);

    if (!layoutTemplate) {
      return {
        supported: false,
        reason: `No layout template available for ${pageType}`
      };
    }

    try {
      // Get preview from layout template if available
      if (typeof layoutTemplate.getPreview === 'function') {
        return {
          supported: true,
          ...layoutTemplate.getPreview(data)
        };
      }

      // Provide basic preview information
      return {
        supported: true,
        estimatedPages: 1,
        sections: ['Header', 'Content', 'Footer'],
        features: ['Professional formatting', 'Print optimized', 'Download ready']
      };

    } catch (error) {
      console.warn('Could not generate layout preview:', error);
      return {
        supported: true,
        estimatedPages: 1,
        sections: ['Content'],
        warning: 'Preview not available'
      };
    }
  }
}

// Export singleton instance
export const pdfLayoutEngine = new PDFLayoutEngine();
export default pdfLayoutEngine;